import { useCallback } from "react";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import {
  clampPatternsWidth,
  PATTERNS_WIDTH_MAX,
  PATTERNS_WIDTH_MIN,
} from "../../lib/dockSizes";
import { useDockResize } from "../../lib/useDockResize";
import { patchUi, uiStore } from "../../store/uiStore";
import { ArrangeDock } from "./ArrangeDock";
import { SampleManagerDock } from "./SampleManagerDock";
import { dockAsideBase, dockFocusRing } from "./dockChrome";

/**
 * Right sidebar column — Arrange and/or Samples, one shared width resize handle.
 */
export function RightSidebar() {
  const patternsOpen = useStoreSelector(uiStore, s => s.patternsOpen);
  const samplesOpen = useStoreSelector(uiStore, s => s.samplesOpen);
  const open = patternsOpen || samplesOpen;
  const storedWidth = useStoreSelector(uiStore, s => s.patternsWidth);

  const commitWidth = useCallback((next: number) => {
    patchUi({ patternsWidth: next });
    getLabActions()?.persistPanels({ patternsWidth: next });
  }, []);

  const {
    size: width,
    dragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    nudge,
  } = useDockResize({
    storedSize: storedWidth,
    clamp: clampPatternsWidth,
    onCommit: commitWidth,
    axis: "x",
    edge: "start",
  });

  return (
    <aside
      id="right-sidebar"
      className={`right-sidebar relative isolate ${dockAsideBase} ${dockFocusRing} shrink-0 max-[720px]:!w-full ${
        open ? "flex" : "hidden"
      } ${dragging ? "select-none" : ""}`}
      style={{ width }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize right sidebar"
        aria-valuemin={PATTERNS_WIDTH_MIN}
        aria-valuemax={PATTERNS_WIDTH_MAX}
        aria-valuenow={width}
        tabIndex={0}
        className={[
          "absolute top-0 bottom-0 left-0 z-40 w-1.5 cursor-col-resize touch-none",
          "bg-transparent hover:bg-lab-accent/35 active:bg-lab-accent/50",
          "max-[720px]:hidden",
          dragging ? "bg-lab-accent/40" : "",
        ].join(" ")}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={event => {
          const step = event.shiftKey ? 32 : 16;
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            nudge(step);
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            nudge(-step);
          }
        }}
      />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {patternsOpen && (
          <div
            className={
              samplesOpen
                ? "flex min-h-0 min-w-0 flex-[1.15] flex-col overflow-hidden"
                : "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
            }
          >
            <ArrangeDock embedded />
          </div>
        )}
        {samplesOpen && (
          <div
            className={
              patternsOpen
                ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-t border-lab-line"
                : "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
            }
          >
            <SampleManagerDock />
          </div>
        )}
      </div>
    </aside>
  );
}
